"use client";

import React, { useState } from "react";
import Link from "next/link";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Magnetic from "@/components/ui/Magnetic";

interface Project {
  slug: string;
  title: string;
  subtitle: string;
  status: string;
  statusColor: string;
  year: string;
  description: string;
  badges: string[];
  accentColor: string;
}

const projectsList: Project[] = [
  {
    slug: "clickotion",
    title: "Clickotion",
    subtitle: "Agency Client Portal & PM",
    status: "Active",
    statusColor: "bg-indigo-500/20 text-indigo-300 border border-indigo-500/20",
    year: "2026",
    description: "A premium client portal and project management platform built for a marketing & software development agency. Features real-time Chatwoot CRM integration, custom dashboards, and automated Cashfree webhook handling.",
    badges: ["Next.js 15", "PostgreSQL", "Cashfree PG", "Chatwoot API"],
    accentColor: "from-indigo-500/10 to-purple-500/5",
  },
  {
    slug: "streamvision",
    title: "StreamVision",
    subtitle: "Govt Infrastructure",
    status: "Live",
    statusColor: "bg-green-500/20 text-green-400 border border-green-500/20",
    year: "2025",
    description: "Architected a high-concurrency CCTV monitoring system for the Haryana Government. Features low-latency RTSP-to-HLS transcoding and real-time motion forensics.",
    badges: ["FFmpeg", "Node.js", "Docker", "NGINX"],
    accentColor: "from-purple-500/10 to-pink-500/5",
  },
  {
    slug: "studenttracker",
    title: "StudentTracker",
    subtitle: "EdTech Dashboard",
    status: "Completed",
    statusColor: "bg-gray-500/20 text-gray-400 border border-gray-500/20",
    year: "2024",
    description: "A comprehensive student performance monitoring database. Compiles term metrics, attendance tracking, and exports detailed report sheets dynamically.",
    badges: ["Vue.js", "Express.js", "MySQL", "Prisma"],
    accentColor: "from-blue-500/10 to-indigo-500/5",
  }
];

export default function ProjectsPage() {
  const [filter, setFilter] = useState("all");

  const filteredProjects = filter === "all"
    ? projectsList
    : projectsList.filter(p => p.status.toLowerCase() === filter);

  return (
    <>
      <Navbar />
      <main className="min-h-screen pt-32 pb-20 relative z-10">
        {/* Background Grid Pattern */}
        <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(99,102,241,0.04)_1px,transparent_1px),linear-gradient(to_bottom,rgba(99,102,241,0.04)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_50%,#000_70%,transparent_100%)] -z-10 opacity-60" />
        
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-16 text-center">
            <span className="text-xs font-bold tracking-[0.4em] text-indigo-400 uppercase mb-4 block font-mono">
              Portfolio
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-black text-white font-[var(--font-poppins)] tracking-tighter mb-6">
              Digital <span className="gradient-text">Masterpieces</span>
            </h1>
            <p className="text-gray-400 text-sm md:text-base font-medium max-w-xl mx-auto mb-10">
              Case studies detailing core software systems engineering, relational database schemas, external API pipelines, and high-concurrency performance outcomes.
            </p>

            {/* Filter buttons */}
            <div className="flex justify-center gap-3">
              {["all", "active", "live", "completed"].map((type) => (
                <button
                  key={type}
                  onClick={() => setFilter(type)}
                  className={`px-4 py-2 rounded-xl text-xs font-bold uppercase tracking-wider border transition-all duration-300 cursor-pointer ${
                    filter === type
                      ? "bg-indigo-500/15 border-indigo-400 text-white shadow-xl shadow-indigo-500/10"
                      : "bg-white/5 border-white/5 text-gray-400 hover:border-white/10 hover:text-white"
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProjects.map((project, idx) => (
              <motion.div
                key={project.slug}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: idx * 0.1 }}
                className={`bg-white/[0.02] border border-white/5 rounded-[2rem] p-8 hover:border-indigo-500/30 transition-all duration-300 shadow-2xl flex flex-col justify-between`}
              >
                <div>
                  <div className="flex items-center justify-between gap-3 mb-6 font-mono text-[10px]">
                    <span className="text-gray-500">{project.year}</span>
                    <span className={`px-2 py-0.5 rounded font-black uppercase tracking-widest ${project.statusColor}`}>
                      {project.status}
                    </span>
                  </div>

                  <Link href={`/projects/${project.slug}`} className="group">
                    <h2 className="text-2xl font-black text-white group-hover:text-indigo-400 transition-colors duration-300 font-[var(--font-poppins)] tracking-tight leading-snug mb-1">
                      {project.title}
                    </h2>
                    <span className="text-[9px] font-bold text-gray-500 uppercase tracking-[0.2em] block mb-4">{project.subtitle}</span>
                  </Link>

                  <p className="text-gray-400 text-xs leading-relaxed mb-6 font-medium">
                    {project.description}
                  </p>

                  <div className="flex flex-wrap gap-1.5 mb-6">
                    {project.badges.map((badge) => (
                      <span key={badge} className="text-[8px] px-2 py-1 rounded-lg border border-indigo-500/20 bg-indigo-500/5 text-indigo-300 font-black uppercase tracking-widest">
                        {badge}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="flex items-center justify-between border-t border-white/5 pt-4 mt-6">
                  <Magnetic>
                    <Link
                      href={`/projects/${project.slug}`}
                      className="flex items-center gap-2 text-xs font-black uppercase tracking-widest text-indigo-400 hover:text-white transition-colors cursor-pointer"
                    >
                      <span>Read Case Study</span>
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </Link>
                  </Magnetic>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
